import { useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../../../../services/startFirestore';
import { EntityRecord } from '#types/System.types';
import { REQUIRED_SOURCE_ID_MAPPING } from '#types';

interface FindLineItemResult {
  record: EntityRecord | null;
  hasDuplicates: boolean;
  duplicateCount: number;
}

/**
 * Hook to find LineItem entity records by manufacturersProductId AND manufacturersSourceId.
 * Queries Firestore for records where:
 * - fields.manufacturersProductId.value matches the provided code
 * - fields.manufacturersSourceId.value equals 'SignifyAPI'
 * 
 * This ensures we only find products from the Signify API source.
 * 
 * @returns Function that queries for records by product code and source
 */
export const useFindLineItemByProductId = () => {
  const findByProductId = useCallback(async (productCode: string): Promise<FindLineItemResult> => {
    try {
      const recordsRef = collection(firestore, 'entities/Products/records');
      const q = query(
        recordsRef,
        where('fields.manufacturersProductId.value', '==', productCode),
        where('fields.manufacturersSourceId.value', '==', REQUIRED_SOURCE_ID_MAPPING)
      );

      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        docId: doc.id,
        ...doc.data()
      } as EntityRecord));

      const duplicateCount = records.length;
      const hasDuplicates = duplicateCount > 1;

      if (hasDuplicates) {
        console.error(
          `[useFindLineItemByProductId] Multiple LineItem records found with manufacturersProductId="${productCode}" ` +
          `and manufacturersSourceId="${REQUIRED_SOURCE_ID_MAPPING}". Found ${duplicateCount} records. ` +
          `This indicates data inconsistency - the combination should be unique per LineItem.`
        );
      }

      return {
        record: records[0] || null,
        hasDuplicates,
        duplicateCount
      };
    } catch (error) {
      console.error(
        `[useFindLineItemByProductId] Error querying LineItems by manufacturersProductId="${productCode}" ` +
        `and manufacturersSourceId="${REQUIRED_SOURCE_ID_MAPPING}":`,
        error
      );
      throw error;
    }
  }, []);

  return { findByProductId };
};

